import React from "react";
import * as Lucide from "lucide-react";
import { useParams, useNavigate, Navigate } from "react-router-dom";
import { BRAND, BRANDS } from "@/content/landing";
import { PERSONAS } from "@/content/personas";
import { LanguageSwitcher } from "@/i18n/LanguageContext";

const { red: RED, gold: GOLD, charcoal: CHARCOAL } = BRAND.colors;

const startLogin = (route) => {
  const redirect = window.location.origin + (route || "/dashboard");
  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirect)}`;
};

const trackEvent = () => {};  // Persona analytics intentionally no-op; /api/metrics/event whitelist is strict.

function Icon({ name, ...props }) {
  const C = Lucide[name] || Lucide.Sparkles;
  return <C {...props} />;
}

export default function PersonaLanding() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const persona = PERSONAS[slug];

  React.useEffect(() => { trackEvent(`persona_view_${slug}`); }, [slug]);

  if (!persona) return <Navigate to="/" replace />;

  const onPrimary = () => {
    trackEvent(`persona_cta_primary_${slug}`);
    if (persona.external) navigate(persona.route);
    else startLogin(persona.route);
  };

  const recommendedBrands = persona.recommendedBrands
    .map((k) => BRANDS.find((b) => b.key === k))
    .filter(Boolean);

  return (
    <div className="min-h-screen bg-white text-[#09090b]">
      {/* HEADER */}
      <header className="border-b border-zinc-200 bg-white sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-5 lg:px-10 h-16 flex items-center justify-between">
          <button onClick={() => navigate("/")} className="flex items-center gap-3" data-testid="persona-brand-link">
            <img src={BRAND.logo} alt={BRAND.name} className="w-10 h-10 object-contain" />
            <div className="leading-tight text-left">
              <div className="font-display font-black text-base tracking-tight">{BRAND.name}</div>
              <div className="text-[9px] uppercase tracking-widest text-zinc-500">{BRAND.tagline}</div>
            </div>
          </button>
          <div className="flex items-center gap-3">
            <LanguageSwitcher />
            <button
              onClick={onPrimary}
              data-testid="persona-header-cta"
              className="px-4 py-2 text-sm font-semibold text-white hover:opacity-90"
              style={{ background: RED }}
            >
              {persona.ctaPrimary}
            </button>
          </div>
        </div>
      </header>

      {/* HERO */}
      <section className="relative overflow-hidden border-b border-zinc-200" style={{ background: CHARCOAL, color: "white" }}>
        <div className="absolute inset-0 opacity-30">
          {persona.heroImage && <img src={persona.heroImage} alt="" className="w-full h-full object-cover" />}
          <div className="absolute inset-0" style={{ background: `linear-gradient(90deg, ${CHARCOAL}F0 30%, ${CHARCOAL}80 100%)` }} />
        </div>
        <div className="relative max-w-7xl mx-auto px-5 lg:px-10 py-20 lg:py-28">
          <div className="inline-flex items-center gap-2 text-[10px] uppercase tracking-widest font-semibold px-3 py-1.5 border border-white/30" data-testid="persona-audience">
            <span className="w-1.5 h-1.5 inline-block" style={{ background: GOLD }} /> {persona.audienceLabel}
          </div>
          <h1 className="font-display font-black tracking-tight text-4xl sm:text-6xl lg:text-7xl mt-6 leading-[1.05]" data-testid="persona-headline">
            {persona.headline}<br />
            <span style={{ color: GOLD }}>{persona.headlineAccent}</span>
          </h1>
          <p className="mt-6 text-white/85 text-base sm:text-lg max-w-2xl" data-testid="persona-sub">{persona.sub}</p>
          <div className="mt-8 flex flex-wrap gap-3">
            <button
              onClick={onPrimary}
              data-testid="persona-cta-primary"
              className="inline-flex items-center gap-2 text-white px-6 py-3 font-semibold hover:opacity-90"
              style={{ background: RED }}
            >
              {persona.ctaPrimary} <Lucide.ArrowRight className="w-4 h-4" />
            </button>
            <button
              onClick={() => { trackEvent(`persona_cta_secondary_${slug}`); navigate("/"); }}
              data-testid="persona-cta-secondary"
              className="inline-flex items-center gap-2 bg-white text-zinc-900 px-6 py-3 font-semibold hover:bg-zinc-200"
            >
              {persona.ctaSecondary}
            </button>
          </div>
        </div>
      </section>

      {/* BENEFITS */}
      <section className="border-b border-zinc-200 bg-white">
        <div className="max-w-7xl mx-auto px-5 lg:px-10 py-16 lg:py-24">
          <div className="text-center mb-12">
            <div className="tag inline-flex"><span className="w-1.5 h-1.5 inline-block" style={{ background: RED }} /> Why MIRESCO</div>
            <h2 className="font-display font-black text-3xl sm:text-5xl mt-4 tracking-tight">Built for {persona.audienceLabel.replace("For ", "").toLowerCase()}.</h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="persona-benefits">
            {persona.benefits.map((b, i) => (
              <div key={b.title} className="border border-zinc-200 p-6 hover:border-[#C8102E] hover:shadow-[6px_6px_0_0_#C8102E] transition-all">
                <div className="w-12 h-12 grid place-items-center text-white" style={{ background: i % 2 === 0 ? RED : CHARCOAL }}>
                  <Icon name={b.icon} className="w-6 h-6" />
                </div>
                <h3 className="font-display font-black text-lg mt-5 tracking-tight">{b.title}</h3>
                <p className="text-sm text-zinc-600 mt-2 leading-snug">{b.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* RECOMMENDED OPPORTUNITIES */}
      <section className="border-b border-zinc-200" style={{ background: "#FAFAFA" }}>
        <div className="max-w-7xl mx-auto px-5 lg:px-10 py-16 lg:py-24">
          <div className="mb-10">
            <div className="tag"><span className="w-1.5 h-1.5 inline-block" style={{ background: GOLD }} /> Top picks for you</div>
            <h2 className="font-display font-black text-3xl sm:text-5xl mt-4 tracking-tight">Where to start.</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5" data-testid="persona-recommended">
            {recommendedBrands.map((b) => (
              <div
                key={b.key}
                className="group bg-white border border-zinc-200 overflow-hidden hover:border-[#C8102E] hover:shadow-[8px_8px_0_0_#C8102E] transition-all cursor-pointer"
                onClick={() => { trackEvent(`persona_brand_${slug}_${b.key}`); if (b.showcaseUrl) navigate(b.showcaseUrl); else startLogin("/opportunities"); }}
                data-testid={`persona-brand-${b.key}`}
              >
                <div className="h-44 relative overflow-hidden" style={{ background: b.fallback }}>
                  {b.image && <img src={b.image} alt={b.name} className="absolute inset-0 w-full h-full object-cover" loading="lazy" />}
                  <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, rgba(0,0,0,0.1) 0%, rgba(0,0,0,0.55) 100%)" }} />
                </div>
                <div className="p-5">
                  <h3 className="font-display font-black text-lg tracking-tight">{b.name}</h3>
                  <div className="text-[11px] uppercase tracking-widest font-semibold mt-1" style={{ color: RED }}>{b.tagline}</div>
                  <p className="text-sm text-zinc-600 mt-3">{b.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* OPPORTUNITY TV STRIP — drives signups via real story videos */}
      <section className="border-b border-zinc-200 relative overflow-hidden" style={{ background: CHARCOAL, color: "white" }}>
        <div className="absolute -top-20 -right-20 w-72 h-72 rounded-full blur-3xl opacity-25" style={{ background: RED }} />
        <div className="relative max-w-7xl mx-auto px-5 lg:px-10 py-16 lg:py-20 grid grid-cols-1 lg:grid-cols-[1fr_auto] items-center gap-10">
          <div>
            <div className="inline-flex items-center gap-2 text-[10px] uppercase tracking-widest font-semibold px-3 py-1.5 bg-white/10 backdrop-blur border border-white/15" data-testid="persona-tv-tag">
              <span className="relative flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full opacity-75" style={{ background: GOLD }} />
                <span className="relative inline-flex rounded-full h-2 w-2" style={{ background: GOLD }} />
              </span>
              Live · Opportunity TV
            </div>
            <h2 className="font-display font-black text-3xl sm:text-5xl mt-5 tracking-tight leading-[1.05]">
              Watch real Malaysians<br /><span style={{ color: GOLD }}>making it happen.</span>
            </h2>
            <p className="mt-4 text-white/85 max-w-xl text-base sm:text-lg">
              Hear the actual stories — how they started, how much they earn, and exactly what they sell. No filters, no fluff.
            </p>
            <button
              onClick={() => { trackEvent(`persona_tv_${slug}`); navigate("/tv"); }}
              data-testid="persona-tv-cta"
              className="mt-7 inline-flex items-center gap-2 text-white px-6 py-3 font-bold hover:opacity-90"
              style={{ background: RED }}
            >
              Watch Opportunity TV <Lucide.Play className="w-4 h-4" />
            </button>
          </div>
          <div
            className="relative aspect-[9/16] w-44 sm:w-52 lg:w-60 bg-black overflow-hidden mx-auto cursor-pointer group border-2 border-white/15"
            onClick={() => navigate("/tv")}
            data-testid="persona-tv-mockup"
          >
            <div className="absolute inset-0 bg-gradient-to-br from-zinc-800 via-zinc-900 to-black" />
            <div className="absolute inset-0 grid place-items-center">
              <div className="w-14 h-14 grid place-items-center bg-white/15 backdrop-blur border border-white/30 group-hover:bg-white/25 transition-colors">
                <Lucide.Play className="w-6 h-6 text-white fill-current" />
              </div>
            </div>
            <div className="absolute bottom-3 left-3 right-3 text-[10px] uppercase tracking-widest font-bold text-white/90">
              60-sec stories →
            </div>
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="relative overflow-hidden" style={{ background: RED, color: "white" }}>
        <div className="relative max-w-7xl mx-auto px-5 lg:px-10 py-20 text-center">
          <h2 className="font-display font-black text-4xl sm:text-6xl tracking-tight">Ready to get started?</h2>
          <p className="mt-5 text-white/90 text-base sm:text-lg max-w-2xl mx-auto">{persona.sub}</p>
          <button
            onClick={onPrimary}
            data-testid="persona-final-cta"
            className="mt-10 inline-flex items-center gap-2 bg-white px-6 py-3 font-semibold hover:bg-zinc-100"
            style={{ color: RED }}
          >
            {persona.ctaPrimary} <Lucide.ArrowRight className="w-4 h-4" />
          </button>
        </div>
      </section>

      <footer className="border-t border-zinc-200 bg-white">
        <div className="max-w-7xl mx-auto px-5 lg:px-10 py-6 flex flex-wrap items-center justify-between gap-3 text-xs text-zinc-500">
          <button onClick={() => navigate("/")} className="hover:text-[#C8102E]">← Back to homepage</button>
          <span>© {new Date().getFullYear()} {BRAND.name}. All rights reserved.</span>
        </div>
      </footer>
    </div>
  );
}
